import type { ReactNode } from 'react';
import { Box, Container, Stack } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  AgronodAlert,
  AgronodCard,
  AgronodChip,
  AgronodIcon,
  AgronodTypography,
  LoaderLinear,
  Tooltip,
} from '@agronod/mui-components';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { useToken } from '../../utils/hooks';
import {
  ApiError,
  avvikelseText,
  gardTitel,
  INRIKTNING_LABEL,
  intervallText,
  MATT_STATUS_TEXT,
  MattStatus,
  modellLabel,
  RevisionGard,
  RevisionListResponse,
  RevisionMatt,
  RevisionMattUtfall,
  RevisionModell,
  RevisionModellRad,
  RevisionRevealedResponse,
  verifieringApi,
} from '../../utils/api';
import { revisionKeys, useRevisionScope, useRollForKund } from './revisionScope';
import { RouterLink } from '../../components/RouterLink';
import { ForetagBorttaget } from './ForetagBorttaget';

interface Props {
  /** Varifrån gården hämtas: revisionsledarens lista eller de avpseudonymiserade. */
  kalla: 'list' | 'revealed';
}

const typText = (typ: RevisionMatt['typ']) =>
  typ === 'kontrollpunkt' ? 'Kontrollpunkt' : 'Nyckeltal';

const HjalpIkon = ({ title, label }: { title: string; label: string }) => (
  <Tooltip title={title}>
    {/* span: ikonen vidarebefordrar ingen ref, Tooltip behöver en */}
    <Box
      component="span"
      tabIndex={0}
      aria-label={label}
      sx={{
        display: 'inline-flex',
        verticalAlign: 'middle',
        color: 'icon.secondary',
        cursor: 'help',
        borderRadius: 0.5,
        '&:focus-visible': { outline: 2, outlineColor: 'text.primary', outlineOffset: 2 },
      }}
    >
      <AgronodIcon name="infoOutlined" fontSize="small" />
    </Box>
  </Tooltip>
);

/** Uppgift i faktarutan: etikett över värde. */
const Fakta = ({ rubrik, varde, hjalp }: { rubrik: string; varde: ReactNode; hjalp?: string }) => (
  <Stack spacing={0.5}>
    <AgronodTypography
      variant="caption"
      sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      {rubrik}
      {hjalp && <HjalpIkon title={hjalp} label={`Om ${rubrik.toLowerCase()}`} />}
    </AgronodTypography>
    <AgronodTypography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {varde}
    </AgronodTypography>
  </Stack>
);

/** Rubrik inne i avvikelsekortet: valfri ikon + fet brödtext. */
const Delrubrik = ({ ikon, children }: { ikon?: ReactNode; children: string }) => (
  <AgronodTypography
    variant="body2"
    component="h4"
    sx={{ m: 0, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
  >
    {ikon}
    {children}
  </AgronodTypography>
);

const Stycken = ({ text }: { text: string }) => (
  <>
    {text.split(/\n{2,}/).map((stycke, i) => (
      <AgronodTypography
        key={i}
        variant="body2"
        sx={{ lineHeight: '22px', whiteSpace: 'pre-line', textWrap: 'pretty' }}
      >
        {stycke}
      </AgronodTypography>
    ))}
  </>
);

/**
 * Statuspill med pil, i samma färger som listans symboler. Tooltipen
 * förklarar varför den faktiska siffran inte visas.
 */
const StatusPill = ({ status }: { status: MattStatus }) => {
  const text = MATT_STATUS_TEXT[status];
  return (
    <Tooltip
      title={`Du ser bara att värdet ligger ${status === 'over' ? 'över' : 'under'} intervallet, inte lantbrukarens faktiska siffra. Agronod delar inte vidare lantbrukares detaljerade data. Kontakta lantbrukaren direkt för att ta del av siffrorna.`}
    >
      <Box
        component="span"
        tabIndex={0}
        aria-label={`${text}. Varför visas inte den faktiska siffran?`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          pl: 1,
          pr: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: 'warning.pastel',
          color: 'warning.dark',
          typography: 'body2',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          cursor: 'help',
          '&:focus-visible': { outline: 2, outlineColor: 'text.primary', outlineOffset: 2 },
        }}
      >
        {status === 'over' ? (
          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
        )}
        {text}
      </Box>
    </Tooltip>
  );
};

/** Ett avvikande mått: namn, intervall, status och förklaringstexterna. */
const AvvikelseKort = ({
  matt,
  status,
  modell,
  visaPlatshallare,
}: {
  matt: RevisionMatt;
  status: MattStatus;
  modell: string;
  /** Platshållaren är ett internt meddelande – visas bara för revisionsledaren. */
  visaPlatshallare: boolean;
}) => {
  const intervall = intervallText(matt);
  const texterSaknas =
    visaPlatshallare && (!matt.verifieraSiffran || !matt.omNyckeltalet);
  const omRubrik = matt.typ === 'kontrollpunkt' ? 'Om kontrollpunkten' : 'Om nyckeltalet';
  return (
    <AgronodCard component="article" sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        {/* Två fasta kolumner: namnet bryter rad i sin kolumn, status och
            intervall står alltid till höger om det (ingen omflödning). */}
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
        >
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <AgronodTypography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: 0.5, lineHeight: 1.4 }}
            >
              {typText(matt.typ)} · {modellLabel(modell)}
            </AgronodTypography>
            <AgronodTypography
              variant="subtitle1"
              component="h3"
              sx={{ m: 0, overflowWrap: 'anywhere' }}
            >
              {matt.visningsnamn}
            </AgronodTypography>
          </Stack>
          <Stack
            spacing={1}
            sx={{ alignItems: 'flex-end', textAlign: 'right', flexShrink: 0, maxWidth: '55%' }}
          >
            <StatusPill status={status} />
            {intervall && (
              <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
                Normalintervall: {intervall}
                {matt.enhet ? ` ${matt.enhet}` : ''}
              </AgronodTypography>
            )}
            {matt.intervallBeskrivning && (
              <AgronodTypography
                variant="caption"
                sx={{ color: 'text.secondary', whiteSpace: 'pre-line', textWrap: 'pretty' }}
              >
                {matt.intervallBeskrivning}
              </AgronodTypography>
            )}
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 2,
          }}
        >
          {/* 24 px mellan sektionerna, 8 px mellan rubrik och text */}
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Delrubrik
                ikon={
                  <Box component="span" sx={{ display: 'inline-flex', color: 'info.main' }}>
                    <AgronodIcon name="infoOutlined" fontSize="small" />
                  </Box>
                }
              >
                {omRubrik}
              </Delrubrik>
              {matt.omNyckeltalet ? (
                <Stycken text={matt.omNyckeltalet} />
              ) : (
                <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
                  Förklaringstext saknas ännu.
                </AgronodTypography>
              )}
            </Stack>
            <Stack spacing={1}>
              <Delrubrik>Hur verifierar man siffran?</Delrubrik>
              {matt.verifieraSiffran ? (
                <Stycken text={matt.verifieraSiffran} />
              ) : (
                <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
                  Förklaringstext saknas ännu.
                </AgronodTypography>
              )}
            </Stack>
          </Stack>
          {matt.baserasPa.length > 0 && (
            <Box
              sx={{
                bgcolor: 'background.default',
                borderRadius: 1,
                p: 2,
                alignSelf: 'start',
              }}
            >
              <AgronodTypography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 1 }}
              >
                Baseras på dessa uppgifter i beräkningen
              </AgronodTypography>
              <Box
                component="ul"
                sx={{ m: 0, pl: 2, typography: 'body2', '& li + li': { mt: 0.5 } }}
              >
                {matt.baserasPa.map((uppgift, i) => (
                  <li key={i}>{uppgift}</li>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {texterSaknas && (
          <Stack
            direction="row"
            useFlexGap
            sx={{ alignItems: 'center', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap' }}
          >
            <AgronodChip color="secondary" size="small" label="Platshållare" />
            <AgronodTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Förklaringstext saknas och läggs in av beräkningsteamet under
              Verifiering › Metodparametrar i backoffice.
            </AgronodTypography>
          </Stack>
        )}
      </Stack>
    </AgronodCard>
  );
};

/**
 * Detaljvy per gård (kravspec v4 5.3, K6): granskningsunderlag med faktaruta
 * (identitet efter avpseudonymisering, inriktning, år, avvikelsemått per
 * modell) och ett kort per mått utanför förväntat intervall med
 * förklaringstexter från metodparametrarna. Inga datapunkter visas. Mått
 * inom intervall och mått utan värde listas kort längst ned.
 */
const GardDetaljPage = ({ kalla }: Props) => {
  const { scope, basePath } = useRevisionScope();
  const { nummer } = useParams();
  const { accessToken } = useToken();
  const {
    roll,
    endastPseudonymiserat,
    dataskyddGodkant,
    isPending: rollPending,
  } = useRollForKund(scope?.kundId);

  const nummerNumber = Number(nummer);
  const giltig = !!scope && Number.isInteger(nummerNumber) && nummerNumber >= 1;
  const fromList = kalla === 'list';

  const { data, isPending, error } = useQuery({
    queryKey: scope
      ? fromList
        ? revisionKeys.list(scope)
        : revisionKeys.revealed(scope)
      : ['revision-detalj'],
    queryFn: (): Promise<RevisionListResponse | RevisionRevealedResponse> =>
      fromList
        ? verifieringApi.getRevisionList(accessToken!, scope!)
        : verifieringApi.getRevisionRevealed(accessToken!, scope!),
    enabled:
      !!accessToken &&
      giltig &&
      dataskyddGodkant &&
      (!fromList || (roll === 'REVISIONSLEDARE' && !endastPseudonymiserat)),
    retry: false,
  });

  if (!giltig) {
    return <Navigate to="/" />;
  }
  // AS-4231: dataskyddsvillkoren godkänns i kundvalet innan kunden öppnas.
  if (!rollPending && !dataskyddGodkant) {
    return <Navigate to="/" replace />;
  }
  if (
    fromList &&
    !rollPending &&
    (roll !== 'REVISIONSLEDARE' || endastPseudonymiserat)
  ) {
    return (
      <Navigate to={`${basePath}/avpseudonymiserade/gard/${nummerNumber}`} replace />
    );
  }

  const listUrl = fromList ? basePath : `${basePath}/avpseudonymiserade`;
  const brodsmulor = (sista: string) => (
    <Stack
      component="nav"
      aria-label="Brödsmulor"
      direction="row"
      spacing={0.75}
      sx={{ alignItems: 'center', color: 'text.secondary', typography: 'body2', mb: 3 }}
    >
      <RouterLink to={listUrl}>Gårdar för revision</RouterLink>
      <ChevronRightIcon fontSize="small" aria-hidden />
      <span>{sista}</span>
    </Stack>
  );

  if (!accessToken || rollPending || isPending) {
    return <LoaderLinear color="primary" isGlobal={true} />;
  }

  if (error) {
    const severity =
      error instanceof ApiError && error.status === 403 ? 'info' : 'error';
    return (
      <Container maxWidth={false} sx={{ maxWidth: 1040, pt: 4 }}>
        {brodsmulor(`Gård ${nummerNumber}`)}
        <AgronodAlert severity={severity}>{error.message}</AgronodAlert>
      </Container>
    );
  }

  const gard: RevisionGard | undefined = data.gardar.find(
    (g) => g.nummer === nummerNumber,
  );
  if (!gard) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: 1040, pt: 4 }}>
        {brodsmulor(`Gård ${nummerNumber}`)}
        <AgronodAlert severity="error">
          Gård {nummerNumber} finns inte i {fromList ? 'listan' : 'det avpseudonymiserade urvalet'}.
        </AgronodAlert>
      </Container>
    );
  }

  // Gårdens egna rader är källan (fryst vid låsning); definitionerna från
  // metodparametrarna bidrar bara med texter och målnamn, och kan saknas
  // när backoffice-api inte svarat.
  const definitioner: RevisionModell[] = data.modeller ?? [];
  const definition = (modell: string, falt: string): RevisionMatt | undefined =>
    definitioner.find((m) => m.modell === modell)?.matt.find((m) => m.falt === falt);
  const malnamn = (modell: string) =>
    definitioner.find((m) => m.modell === modell)?.malnamn ?? 'klimatavtryck';
  const bedomda: { modell: string; rad: RevisionModellRad }[] = gard.modeller.map(
    (rad) => ({ modell: rad.modell, rad: { ...rad, matt: rad.matt ?? [] } }),
  );
  const medStatus = (filter: (status: MattStatus) => boolean) =>
    bedomda.flatMap(({ modell, rad }) =>
      rad.matt.filter((m) => filter(m.status)).map((utfall) => ({ modell, utfall })),
    );
  const avvikande = medStatus((s) => s === 'over' || s === 'under');
  const inom = medStatus((s) => s === 'inom');
  const saknas = medStatus((s) => s === 'saknas');
  // Intervall, namn och typ från gårdens rad (det den bedömdes mot); texter live.
  const tillDefinition = (modell: string, utfall: RevisionMattUtfall): RevisionMatt => {
    const texter = definition(modell, utfall.falt);
    const { falt, visningsnamn, typ, min, max } = utfall;
    return {
      falt,
      visningsnamn,
      typ,
      min,
      max,
      enhet: texter?.enhet ?? null,
      verifieraSiffran: texter?.verifieraSiffran ?? null,
      baserasPa: texter?.baserasPa ?? [],
      omNyckeltalet: texter?.omNyckeltalet ?? null,
      intervallBeskrivning: texter?.intervallBeskrivning ?? null,
    };
  };
  const arLedare = roll === 'REVISIONSLEDARE' && !endastPseudonymiserat;

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1040, pt: 4, pb: 8 }}>
      {brodsmulor(gardTitel(gard))}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <AgronodTypography variant="h4" component="h1">
          {gardTitel(gard)}
        </AgronodTypography>
        <AgronodTypography variant="body1" sx={{ color: 'text.secondary' }}>
          Granskningsunderlag – avvikande nyckeltal och kontrollpunkter.
        </AgronodTypography>
      </Stack>

      <AgronodCard sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 3,
          }}
        >
          <Fakta
            rubrik="Organisationsnummer"
            varde={
              gard.identitet
                ? (gard.identitet.orgnr ??
                  (gard.identitet.foretagBorttaget ? <ForetagBorttaget /> : 'Saknas'))
                : 'Dolt tills urvalet bekräftats'
            }
          />
          {gard.identitet && <Fakta rubrik="Pseudonym" varde={`Gård ${gard.nummer}`} />}
          <Fakta
            rubrik="Produktionsinriktning"
            varde={`${INRIKTNING_LABEL[scope.inriktning]}${
              bedomda.length > 0
                ? ` – ${bedomda.map(({ modell }) => modellLabel(modell).toLowerCase()).join(', ')}`
                : ''
            }`}
          />
          <Fakta rubrik="Beräkningsår" varde={String(scope.ar)} />
          {bedomda.map(({ modell, rad }) => (
            <Fakta
              key={modell}
              rubrik={`Avvikelse ${modellLabel(modell).toLowerCase()}`}
              hjalp={`Skillnaden mellan gårdens beräknade ${malnamn(modell)} och modellens prediktion. Listan är sorterad med störst avvikelse först.`}
              varde={avvikelseText(rad.absDifferens)}
            />
          ))}
          {bedomda.length === 0 && (
            <Fakta rubrik="Avvikelse" varde="Ingen bedömning – gården saknar underlag för modellerna." />
          )}
        </Box>
      </AgronodCard>

      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <AgronodTypography variant="h5" component="h2">
            Avvikelser att gå igenom
          </AgronodTypography>
          <HjalpIkon
            label="Varför ser jag inte lantbrukarens faktiska siffra?"
            title="Agronod delar inte vidare lantbrukares detaljerade data. Kontakta lantbrukaren direkt för att ta del av siffrorna. En flagga betyder att ett värde ska stämmas av, inte att beräkningen är fel."
          />
        </Stack>

        {avvikande.length === 0 ? (
          <AgronodAlert severity="info">
            Inga nyckeltal eller kontrollpunkter ligger utanför förväntat intervall.
          </AgronodAlert>
        ) : (
          avvikande.map(({ modell, utfall }) => (
            <AvvikelseKort
              key={`${modell}:${utfall.falt}`}
              modell={modell}
              status={utfall.status}
              matt={tillDefinition(modell, utfall)}
              visaPlatshallare={arLedare}
            />
          ))
        )}

        {(inom.length > 0 || saknas.length > 0) && (
          <AgronodCard sx={{ p: 3, mt: 1 }}>
            <Stack spacing={2}>
              {inom.length > 0 && (
                <Box>
                  <AgronodTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 1 }}
                  >
                    Inom förväntat intervall
                  </AgronodTypography>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {inom.map(({ modell, utfall }) => (
                      <Tooltip
                        key={`${modell}:${utfall.falt}`}
                        title={`${modellLabel(modell)} · ${MATT_STATUS_TEXT[utfall.status]}${
                          intervallText(utfall) ? ` (${intervallText(utfall)})` : ''
                        }`}
                      >
                        <AgronodChip size="small" color="success" label={utfall.visningsnamn} />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
              {saknas.length > 0 && (
                <Box>
                  <AgronodTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 1 }}
                  >
                    Värde saknas
                  </AgronodTypography>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {saknas.map(({ modell, utfall }) => (
                      <Tooltip key={`${modell}:${utfall.falt}`} title={`${modellLabel(modell)} · ${MATT_STATUS_TEXT.saknas}`}>
                        <AgronodChip size="small" color="secondary" label={utfall.visningsnamn} />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </AgronodCard>
        )}
      </Stack>
    </Container>
  );
};

export default GardDetaljPage;
